import toast from 'react-hot-toast';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { IFieldValue } from 'Scripts/Slices/loginPage';

import { executeFunctionByName } from 'Utils/customFormHandlers';

import {
    ICheckboxModel,
    IRadioModel,
    ISelectModel,
    ITextAreaModel,
    ITextModel,
} from 'Types/Litium';
import CheckboxField from 'Components/Forms/CheckboxField';
import HiddenField from 'Components/Forms/HiddenField';
import InputField from 'Components/Forms/InputField';
import SelectField from 'Components/Forms/SelectField';
import TextAreaField from 'Components/Forms/TextAreaField';

export interface ValidationSchemaItem {
    isRequiredCriteriaMet: boolean;
    isValid: boolean;
    isVisible: boolean;
    name: string;
}

export type ValidationSchema = Array<ValidationSchemaItem> | undefined;

export type InputModel =
    | ITextModel
    | ITextAreaModel
    | ICheckboxModel
    | IRadioModel
    | ISelectModel;

export type InputModelArray = (
    | ITextModel
    | ITextAreaModel
    | ICheckboxModel
    | IRadioModel
    | ISelectModel
)[];

const isTextModel = (field: InputModel): field is ITextModel =>
    field.type === 'text' && 'validation' in field;

const isTextAreaModel = (field: InputModel): field is ITextAreaModel =>
    field.type === 'textarea' && 'isToggled' in field;

const isCheckboxModel = (field: InputModel): field is ICheckboxModel =>
    field.type === 'checkbox' && 'checked' in field;

const isRadioModel = (field: InputModel): field is IRadioModel =>
    field.type === 'radio' && 'checked' in field;

export const isSelectModel = (field: InputModel): field is ISelectModel =>
    field.type === 'select' && 'options' in field;

export const validateFormFields = (
    fields: InputModelArray,
    setValidationObject?: React.Dispatch<
        React.SetStateAction<ValidationSchemaItem[]>
    >
) => {
    if (setValidationObject) setValidationObject([]);
    const validationSchema: ValidationSchema = fields.map(field => {
        const hasDepandancy = field.isDependentOn.length > 0;
        const isDependentOnChecked = Boolean(
            field.isDependentOn.length &&
                fields.some(
                    f =>
                        f.name === field.isDependentOn &&
                        isCheckboxModel(f) &&
                        f.checked
                )
        );

        const isVisible =
            (hasDepandancy &&
                isDependentOnChecked &&
                ((isSelectModel(field) && field.visible) ||
                    !isSelectModel(field))) ||
            (!hasDepandancy &&
                ((isSelectModel(field) && field.visible) ||
                    !isSelectModel(field)));

        const hasValue = () => {
            if (field.required) {
                if (isSelectModel(field)) {
                    return (
                        field.value !== '' ||
                        field.value !== undefined ||
                        field.value === null
                    );
                }
                if (isCheckboxModel(field)) {
                    return field.checked;
                }
                return field.value?.length > 0;
            }
            return true;
        };
        const regx = new RegExp(
            isTextModel(field) || isTextAreaModel(field)
                ? field.validation
                : '^.*$'
        );
        const valid = regx.test(field.value);
        return {
            isRequiredCriteriaMet: hasValue(),
            isValid: field.value?.length > 0 ? valid : true,
            isVisible,
            name: field.name,
        };
    });

    if (
        validationSchema.some(
            a => a.isVisible && (!a.isRequiredCriteriaMet || !a.isValid)
        )
    ) {
        if (setValidationObject) setValidationObject(validationSchema);
        return false;
    }
    return true;
};

interface FormResultData {
    [key: string]: any;
    redirectUrl?: string;
}

interface ErrorObject {
    [key: string]: unknown;
    errors: {
        [key: string]: string;
    };
}

export const handleFormPost = (
    data: FormResultData,
    error: FetchBaseQueryError | SerializedError
) => {
    if (error && 'data' in error) {
        const errorObject = error?.data as ErrorObject;
        if (errorObject?.errors) {
            Object.values(errorObject.errors)
                .flat()
                .forEach((message: string) => toast.error(message));
        } else if (errorObject) {
            Object.values(errorObject)
                .flat()
                .forEach((message: string) => toast.error(message));
        } else {
            toast.error(`Something went wrong: ${error.status}`);
            console.info(error);
        }
    }
    if (data && 'redirectUrl' in data) {
        window.location.href = data.redirectUrl;
    }
};

export interface IInputValidation {
    isRequiredCriteriaMet: boolean;
    isValid: boolean;
    isVisible: boolean;
    name: string;
}

const WrapperTag = ({
    as: Tag,
    children,
    className = '',
}: {
    as?: React.ElementType;
    children: React.ReactElement;
    className?: string;
}) => (Tag ? <Tag className={className}>{children}</Tag> : children);

const RenderFormField = ({
    field,
    form,
    validation,
    loading = false,
    handleInputChange,
    cssStyles,
    as,
}: {
    field: InputModel;
    loading?: boolean;
    form: InputModel[];
    validation: ValidationSchema;
    handleInputChange: (data: IFieldValue) => void;
    cssStyles?: string;
    as?: React.ElementType;
}) => {
    if (
        field.isDependentOn.length &&
        form.some(
            f =>
                f.name === field.isDependentOn &&
                isCheckboxModel(f) &&
                f.checked
        )
    ) {
        return;
    }

    const error = validation.find(element => element.name === field.name);
    switch (field.type) {
        case 'number':
        case 'text':
        case 'email':
        case 'password':
            return (
                <WrapperTag as={as}>
                    <InputField
                        cssStyles={cssStyles}
                        field={field as ITextModel}
                        loading={loading}
                        handleInputChange={handleInputChange}
                        error={error}
                    />
                </WrapperTag>
            );
        case 'textarea':
            return (
                <WrapperTag as={as}>
                    <TextAreaField
                        cssStyles={cssStyles}
                        field={field as ITextAreaModel}
                        loading={loading}
                        handleInputChange={handleInputChange}
                        error={error}
                    />
                </WrapperTag>
            );
        case 'hidden':
            return (
                <WrapperTag as={as} className="invisible">
                    <HiddenField field={field} />
                </WrapperTag>
            );
        case 'checkbox':
        case 'htmlCheckbox':
        case 'htmlModalCheckbox':
            return (
                <WrapperTag>
                    <CheckboxField
                        field={field as ICheckboxModel}
                        renderAsHtml={
                            field.type === 'htmlCheckbox' ||
                            field.type === 'htmlModalCheckbox'
                        }
                        loading={loading}
                        handleInputChange={handleInputChange}
                        executeFunctionByName={executeFunctionByName}
                    />
                </WrapperTag>
            );
        case 'select':
            return (
                isSelectModel(field) &&
                field.visible && (
                    <WrapperTag>
                        <SelectField
                            field={field}
                            loading={loading}
                            handleInputChange={handleInputChange}
                            executeFunctionByName={executeFunctionByName}
                            error={error}
                        />
                    </WrapperTag>
                )
            );
        default:
            return null;
    }
};

export default RenderFormField;
